<template>
  <div
    v-if="items.length"
    class="core-image-slider"
  >
    <CoreImageLightbox
      ref="lightboxRef"
      :items="items"
    />

    <CoreSwiper
      class="core-image-slider__swiper"
      :slides-per-view="slidesPerView"
      :space-between="spaceBetween"
      :has-navigation="showNavigation"
      :has-pagination="showPagination"
      :free-mode="freeMode"
    >
      <template #slides="{ slideAttrs }">
        <div
          v-for="(item, index) in items"
          :key="`${item.id ?? item.src}-${index}`"
          v-bind="slideAttrs"
        >
          <slot
            name="item"
            :item="item"
            :index="index"
            :open="() => open(index)"
          >
            <article class="core-image-slider__item">
              <button
                type="button"
                class="core-image-slider__preview"
                @click="open(index)"
              >
                <img
                  class="core-image-slider__image"
                  :src="item.previewSrc || item.src"
                  :alt="item.alt || item.title || ''"
                  :title="item.title || undefined"
                  loading="lazy"
                >
              </button>
            </article>
          </slot>
        </div>
      </template>
    </CoreSwiper>
  </div>
</template>

<script lang="ts" setup>
/**
 * Слайдер изображений с открытием в полноэкранном просмотре.
 *
 * Собирает `CoreSwiper` и `CoreImageLightbox`: клик по превью в слайдере
 * открывает те же изображения в полноэкранном просмотре.
 * Подпись (`title`) видна только в полноэкранном просмотре.
 */
import { computed, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import CoreImageLightbox from 'src/core/CoreImageLightbox/CoreImageLightbox.vue'
import CoreSwiper from 'src/core/CoreSwiper/CoreSwiper.vue'
import type { CoreImageSliderItem } from 'src/core/CoreImageSlider/types'

type LightboxExpose = {
  open: (_index: number) => void
}

const props = withDefaults(defineProps<{
  items: CoreImageSliderItem[]
  slidesPerView?: number | 'auto'
  spaceBetween?: number
  hasNavigation?: boolean
  hasPagination?: boolean
  freeMode?: boolean
}>(), {
  slidesPerView: undefined,
  spaceBetween: undefined,
  // eslint-disable-next-line vue/no-boolean-default
  hasNavigation: true,
  // eslint-disable-next-line vue/no-boolean-default
  hasPagination: true,
  freeMode: false,
})

const isMobile = useIsMobile()
const lightboxRef = ref<LightboxExpose | null>(null)

const items = computed(() => props.items.filter(item => Boolean(item.src)))

/** По умолчанию: 1 слайд на mobile, 5 на desktop. */
const slidesPerView = computed(() => (
  props.slidesPerView ?? (isMobile.value ? 1 : 5)
))

/** По умолчанию: 16px на mobile, 20px на desktop. */
const spaceBetween = computed(() => (
  props.spaceBetween ?? (isMobile.value ? 16 : 20)
))

/** Стрелки и пагинация только если слайдов больше, чем видно за раз. */
const hasOverflow = computed(() => {
  const perView = typeof slidesPerView.value === 'number' ? slidesPerView.value : 1

  return items.value.length > perView
})
const showNavigation = computed(() => props.hasNavigation && hasOverflow.value)
const showPagination = computed(() => props.hasPagination && hasOverflow.value)

function open(index: number) {
  lightboxRef.value?.open(index)
}

defineExpose({ open })
</script>

<style scoped lang="scss">
@import 'www/themes/doctors/common/variables';

.core-image-slider {
  $nav-btn-size: 36px;
  $accent: var(--lpu-branding-color-primary, $ui-kit-primary);

  &__item {
    width: 100%;
  }

  &__preview {
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    overflow: hidden;
    border-radius: 20px;
    background-color: var(--lpu-branding-color-background, $ui-kit-bg-primary);
    color: var(--lpu-branding-color-text, $ui-kit-primary);
    line-height: 0;
    text-align: left;
    cursor: pointer;
  }

  &__image {
    display: block;
    width: 100%;
    height: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 20px;
    background-color: var(--lpu-branding-color-background, $ui-kit-bg-primary);
    color: var(--lpu-branding-color-text, $ui-kit-primary);
  }

  :deep(.swiper-button-prev),
  :deep(.swiper-button-next) {
    z-index: 3;
    top: 50%;
    margin-top: 0;
    width: $nav-btn-size;
    height: $nav-btn-size;
    transform: translateY(-50%);
    box-shadow: none !important;
    background-color: $accent !important;

    span {
      font-size: 24px;
      color: $ui-kit-bg-gray-0 !important;
    }
  }

  :deep(.swiper-button-prev) {
    left: 16px;
    right: auto;
  }

  :deep(.swiper-button-next) {
    right: 16px;
    left: auto;
  }

  // Скрыта, но ловит клики: иначе промах по пропавшей стрелке открывает превью
  :deep(.swiper-button-disabled) {
    opacity: 0 !important;
    pointer-events: auto !important;
    cursor: default;
  }

  :deep(.swiper-container > .swiper-pagination-bullets > .swiper-pagination-bullet-active) {
    background-color: $accent;
  }

  // Плейсхолдер высоты до init CoreSwiper (квадратные превью ~208px)
  :deep(.core-swiper:not(.core-swiper--ready) > .swiper-container) {
    min-height: 208px;
    height: 208px;
    visibility: hidden;
  }
}
</style>
